import { Box, Skeleton, Typography } from "@mui/material";
import { TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { motion } from "framer-motion";

/**
 * StatCard — metric display card for dashboards
 *
 * Migrated to CSS custom property tokens (tokens.css).
 * iconColor accepts:
 *   - CSS token prefix starting with "--" (e.g. "--c-cyan", "--c-violet") → uses var(--c-*-bg) soft bg
 *   - Raw hex color (legacy callers) → uses hex + "1a" soft bg
 *
 * Props:
 *   label        — metric name (e.g. "Answered Today")
 *   value        — main number or string (e.g. 142, "98%")
 *   unit         — optional suffix (e.g. "calls", "min")
 *   icon         — MUI icon element
 *   iconColor    — CSS token prefix or hex (default "--c-cyan")
 *   trend        — number, positive = improvement, negative = worsening
 *   trendLabel   — short label next to trend (e.g. "vs yesterday")
 *   trendInvert  — true when lower is better (e.g. "missed calls")
 *   loading      — show skeleton state
 *   onClick      — make card interactive
 *   compact      — smaller padding for operational density
 */
export default function StatCard({
  label,
  value,
  unit,
  icon,
  iconColor = "--c-cyan",
  trend,
  trendLabel = "vs yesterday",
  trendInvert = false,
  loading = false,
  onClick,
  compact = false,
  sx,
}) {
  const isPositive  = trendInvert ? (trend < 0) : (trend > 0);
  const isNeutral   = trend === 0 || trend == null;
  const trendColor  = isNeutral ? "var(--ink-400)" : isPositive ? "var(--success)" : "var(--danger)";
  const TrendIcon   = isNeutral ? TrendingFlat : isPositive ? TrendingUp : TrendingDown;
  const interactive = !!onClick;

  const isToken  = (iconColor ?? "").startsWith("--");
  const iconFill = isToken ? `var(${iconColor})`    : iconColor;
  const iconBg   = isToken ? `var(${iconColor}-bg)` : `${iconColor}1a`;

  const pad = compact ? "14px 16px" : "20px 24px";

  return (
    <Box
      component={motion.div}
      whileHover={interactive ? { y: -2, transition: { duration: 0.15 } } : undefined}
      whileTap={interactive ? { scale: 0.98 } : undefined}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => (e.key === "Enter" || e.key === " ") && onClick(e) : undefined}
      sx={{
        background: "var(--surface-card)",
        border: "1px solid var(--line-100)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--shadow-card)",
        overflow: "hidden",
        cursor: interactive ? "pointer" : "default",
        transition: "box-shadow .2s, transform .2s",
        "&:hover": interactive ? { boxShadow: "var(--shadow-lg)" } : undefined,
        "&:focus-visible": interactive ? {
          outline: "none",
          boxShadow: "0 0 0 2px var(--surface-card), 0 0 0 4px rgba(59,125,240,.45)",
        } : undefined,
        p: 0,
        ...sx,
      }}
    >
      {/* Accent bar */}
      <Box sx={{
        height: 3,
        background: iconFill,
        opacity: 0.75,
        borderRadius: "var(--r-lg) var(--r-lg) 0 0",
      }} />

      <Box sx={{ p: pad, display: "flex", flexDirection: "column", gap: compact ? "10px" : "14px" }}>
        {/* Header row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{
            fontSize: "var(--t-caption)",
            fontWeight: 700,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: "var(--ink-500)",
            lineHeight: 1.3,
          }}>
            {loading ? <Skeleton width={90} /> : label}
          </Typography>

          {icon && (
            <Box sx={{
              width:  compact ? 34 : 40,
              height: compact ? 34 : 40,
              borderRadius: "var(--r-md)",
              background: iconBg,
              color: iconFill,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              "& svg": { fontSize: compact ? 18 : 22 },
            }}>
              {icon}
            </Box>
          )}
        </Box>

        {/* Value row */}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px" }}>
          {loading ? (
            <Skeleton width={80} height={compact ? 36 : 48} />
          ) : (
            <>
              <Typography component="span" sx={{
                fontVariantNumeric: "tabular-nums",
                fontFamily: "var(--font-mono)",
                fontSize:   compact ? "var(--t-kpi-sm)" : "var(--t-kpi)",
                fontWeight: 800,
                lineHeight: 1,
                color: "var(--ink-900)",
                letterSpacing: "-0.03em",
              }}>
                {value ?? "—"}
              </Typography>
              {unit && (
                <Typography component="span" sx={{
                  fontSize:   compact ? "var(--t-body-sm)" : "var(--t-body)",
                  fontWeight: 500,
                  color: "var(--ink-500)",
                  lineHeight: 1,
                }}>
                  {unit}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Trend row */}
        {trend != null && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {loading ? (
              <Skeleton width={100} height={18} />
            ) : (
              <>
                <TrendIcon sx={{ fontSize: 14, color: trendColor, flexShrink: 0 }} />
                <Typography component="span" sx={{
                  fontVariantNumeric: "tabular-nums",
                  fontSize: "var(--t-caption)",
                  fontWeight: 700,
                  color: trendColor,
                  lineHeight: 1,
                }}>
                  {Math.abs(trend)}%
                </Typography>
                <Typography component="span" sx={{
                  fontSize: "var(--t-caption)",
                  color: "var(--ink-400)",
                  lineHeight: 1,
                }}>
                  {trendLabel}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
